import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, Redirect } from 'react-router-dom';
import { getInt } from 'utils/functions';
import ProductSelectionContainer from 'features/offer/edit/productSelection/components/ProductSelectionContainer';
import OfferDocumentsContainer from './documents/OfferDocumentsContainer';
import OfferScoringContainer from 'features/offer/edit/scoring/components/OfferScoringContainer';
import OfferScoringSessionHistoryContainer from 'features/offer/edit/scoringHistory/components/OfferScoringSessionHistoryContainer';
import OfferDecisionComponent from './decision/OfferDecisionComponent';
import OfferCompetenceOrderContainer from './decision/approvalLevels/OfferCompetenceOrderContainer';
import DocumentsHistory from 'features/document/edit/documents/components/DocumentsHistory';
import OfferPaymentPlanContainer from 'features/offer/edit/financials/paymentPlan/components/OfferPaymentPlanContainer';
import OfferCheckListContainer from 'features/offer/edit/checklist/OfferCheckListContainer';
import OfferDecisionConditionContainer from './decision/OfferDecisionConditionContainer';
import DocumentPropertiesContainer from 'features/document/edit/documentProperties/components/DocumentPropertiesContainer';
import PartnerRelationsContainer from './partnerRelations/PartnerRelationsContainer';
import OfferScoringHistoryContainer from 'features/offer/edit/scoringHistory/components/OfferScoringHistoryContainer';
import DocumentVerificationContainer from 'features/document/edit/verification/components/DocumentVerificationContainer';
import StateTransitionsContainer from 'features/document/edit/stateTransitions/components/StateTransitionsContainer';
import OfferPartnerEditQuestionnaireContainer from 'features/offer/edit/client/questionnaire/components/edit/OfferPartnerEditQuestionnaireContainer';
import InsuranceDocumentDetailsContainer from 'features/offer/edit/insurances/components/InsuranceDocumentDetailsContainer';
import documentTypeIds from 'constants/documentTypeIds';
import WrappedFakeText from 'components/common/WrappedFakeText';
import DocumentUploadPreviewModal from 'features/document/edit/documents/components/DocumentUploadPreviewModal';
import DocumentComments from 'features/offer/edit/sidebar/components/comments/DocumentComments';

const WorkflowContainer = React.lazy(() => import('components/offer/edit/workflow/components/WorkflowContainer'));

const SwitcherComponent = ({
  listContainer,
  createContainer,
  container,
  offerFinancialContainer,
  offerClientContainer,
  insuranceDocumentDetailsContainer,
  onChangeActiveStep,
  steps,
  history,
  documentId,
  siteId
}) => (
  <Switch>
    <Route exact path='/offers/:documentId(\d+)/:siteId(\d+)' render={() => <WrappedFakeText onPaper />} />
    <Route exact path='/offers/:documentId(\d+)/:siteId(\d+)/generalInfo' render={listContainer} />
    <Route exact path='/offers/:documentId(\d+)/:siteId(\d+)/assets' render={listContainer} />
    <Route exact path='/offers/new' render={createContainer} />
    {[
      '/offers/:documentId/:siteId/assets/new',
      '/offers/:documentId/:siteId/assets/:documentDetailId',
      '/offers/:documentId/:siteId/generalInfo/new',
      '/offers/:documentId/:siteId/generalInfo/:documentDetailId'
    ].map(path => (
      <Route exact path={path} render={container} key={path} />
    ))}
    <Route exact path='/offers/restructuring/new' render={createContainer} />
    <Route exact path='/offers/facilityFrame/new' render={createContainer} />
    <Route exact path='/offers/:documentId(\d+)/:siteId(\d+)/contract' render={container} />
    <Route exact path='/offers/:documentId(\d+)/:siteId(\d+)/modification' render={offerFinancialContainer} />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/products'
      render={() => <ProductSelectionContainer documentId={documentId} siteId={siteId} />}
    />
    <Route exact path='/offers/:documentId(\d+)/:siteId(\d+)/financials' render={offerFinancialContainer} />
    <Route exact path='/offers/:documentId(\d+)/:siteId(\d+)/insurance' render={InsuranceDocumentDetailsContainer} />
    <Route
      exact
      path='/offers/:documentId/:siteId/insurance/:documentDetailId/:documentDetailSiteId'
      render={insuranceDocumentDetailsContainer}
    />
    <Route
      exact
      path='/offers/:documentId/:siteId/insurance/:documentDetailId/:documentDetailSiteId/:assetInformationId'
      render={insuranceDocumentDetailsContainer}
    />
    <Route exact path='/offers/:documentId(\d+)/:siteId(\d+)/client' render={offerClientContainer} />
    <Route exact path='/offers/:documentId(\d+)/:siteId(\d+)/partner/:partnerId(\-?\d+)' render={offerClientContainer} />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/documents'
      render={() => (
        <OfferDocumentsContainer documentId={documentId} siteId={siteId} onChangeActiveStep={onChangeActiveStep} history={history} />
      )}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/documents/:fileId([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})'
      render={route => <DocumentUploadPreviewModal fileId={route.match.params.fileId} open={true} openFromRoute={true} />}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/scoring'
      render={() => <OfferScoringContainer documentId={documentId} siteId={siteId} history={history} />}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/decision'
      render={() => <OfferDecisionComponent documentId={documentId} siteId={siteId} />}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/paymentPlan'
      render={() => (
        <OfferPaymentPlanContainer
          documentId={documentId}
          siteId={siteId}
          showScheduleImportButton={true}
          steps={steps}
          history={history}
        />
      )}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/document-properties'
      render={() => <DocumentPropertiesContainer documentId={documentId} siteId={siteId} />}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/partnerRelations'
      render={() => <PartnerRelationsContainer documentId={documentId} siteId={siteId} startsExpanded={true} history={history} />}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/state-transitions'
      render={() => <StateTransitionsContainer documentId={documentId} siteId={siteId} documentTypeId={documentTypeIds.Offer} />}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/checklist'
      render={() => <OfferCheckListContainer documentId={documentId} siteId={siteId} />}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/competence-order'
      render={() => <OfferCompetenceOrderContainer documentId={documentId} siteId={siteId} />}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/offer-conditions'
      render={() => <OfferDecisionConditionContainer documentId={documentId} siteId={siteId} />}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/:sessionID([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})/historyscoringsession'
      render={route => (
        <OfferScoringSessionHistoryContainer documentId={documentId} siteId={siteId} sessionID={route.match.params.sessionID} />
      )}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/workflows'
      render={() => <WorkflowContainer documentId={documentId} siteId={siteId} />}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/historyscoring'
      render={() => <OfferScoringHistoryContainer documentId={documentId} siteId={siteId} history={history} />}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/documentHistory/:fileId([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})'
      render={route => <DocumentsHistory fileId={route.match.params.fileId} />}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/verifications'
      render={_route => <DocumentVerificationContainer documentId={documentId} siteId={siteId} />}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/questionnaire/:partnerId(\-?\d+)'
      render={route => (
        <OfferPartnerEditQuestionnaireContainer
          documentId={documentId}
          siteId={siteId}
          partnerId={getInt(route.match.params.partnerId)}
          history={history}
          onChangeActiveStep={onChangeActiveStep}
        />
      )}
    />
    <Route
      exact
      path='/offers/:documentId(\d+)/:siteId(\d+)/comments'
      render={() => <DocumentComments documentId={documentId} siteId={siteId} history={history} fullScreen={true} />}
    />
    {<Route render={() => <Redirect to='/notFound' />} />}
  </Switch>
);

SwitcherComponent.propTypes = {
  listContainer: PropTypes.func.isRequired,
  createContainer: PropTypes.func.isRequired,
  container: PropTypes.func.isRequired,
  offerFinancialContainer: PropTypes.func.isRequired,
  offerClientContainer: PropTypes.func.isRequired,
  insuranceDocumentDetailsContainer: PropTypes.func.isRequired,
  onChangeActiveStep: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  steps: PropTypes.array
};

export default SwitcherComponent;
